/**
 * DateTimePicker – calendar + time with professional styling.
 * Value/onChange: datetime-local string (YYYY-MM-DDTHH:mm).
 * Uses date-fns + Radix Popover only (no react-day-picker).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, format, startOfMonth } from 'date-fns';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

import { DateTimePickerContent } from './DateTimePickerContent';
import {
  clampPastDanish,
  currentDanishTime,
  danishNowLocal,
  parseDateTimePickerValue,
  toDateTimeLocal,
  toTimeString,
} from './date-time-picker-utils';

export interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Min datetime as YYYY-MM-DDTHH:mm */
  min?: string;
  /** Max datetime as YYYY-MM-DDTHH:mm */
  max?: string;
  /** When true, disables calendar days before today (time remains unrestricted) */
  minDateToday?: boolean;
  ariaLabel?: string;
  /** Smaller calendar for tight layouts (e.g. 280px column) */
  compact?: boolean;
}

export const DateTimePicker: FC<DateTimePickerProps> = ({
  value,
  onChange,
  placeholder = 'Not set',
  className,
  disabled,
  min,
  max,
  minDateToday = false,
  ariaLabel = 'Date and time',
  compact = false,
}) => {
  const date = parseDateTimePickerValue(value);
  const [open, setOpen] = useState(false);
  const [timeStr, setTimeStr] = useState(() => (date ? toTimeString(date) : currentDanishTime()));
  const [viewMonth, setViewMonth] = useState(() => date ?? new Date());

  useEffect(() => {
    if (date) {
      setTimeStr(toTimeString(date));
    } else if (!value?.trim()) {
      setTimeStr(currentDanishTime());
    }
  }, [value]);

  const minDate = useMemo(() => {
    if (minDateToday) return parseDateTimePickerValue(danishNowLocal().slice(0, 10) + 'T00:00');
    return min ? parseDateTimePickerValue(min) : undefined;
  }, [minDateToday, min]);
  const maxDate = max ? parseDateTimePickerValue(max) : undefined;

  const minTimeForInput = useMemo(() => {
    if (!minDateToday || !date) return undefined;
    const dkNow = danishNowLocal();
    const selDate = toDateTimeLocal(date).slice(0, 10);
    return selDate === dkNow.slice(0, 10) ? dkNow.slice(11) : undefined;
  }, [minDateToday, date]);

  const displayText = date ? format(date, 'dd/MM/yyyy HH:mm') : placeholder;

  const calendarDays = useMemo(() => {
    const start = startOfMonth(viewMonth);
    const startPad = start.getDay() === 0 ? 6 : start.getDay() - 1;
    const from = addDays(start, -startPad);
    const days: Date[] = [];
    let d = from;
    while (days.length < 42) {
      days.push(d);
      d = addDays(d, 1);
    }
    return days;
  }, [viewMonth]);

  const emitDateTime = useCallback(
    (combined: Date) => {
      let dtLocal = toDateTimeLocal(combined);
      let t = toTimeString(combined);
      if (minDateToday) {
        const c = clampPastDanish(dtLocal);
        dtLocal = c.value;
        t = c.timeStr;
      }
      setTimeStr(t);
      onChange(dtLocal);
    },
    [minDateToday, onChange],
  );

  const handleDaySelect = useCallback(
    (d: Date) => {
      const [h, m] = timeStr.split(':').map(Number);
      emitDateTime(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h || 0, m || 0));
    },
    [timeStr, emitDateTime],
  );

  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const [h, m] = e.target.value.split(':').map(Number);
      const base = date ?? new Date();
      emitDateTime(new Date(base.getFullYear(), base.getMonth(), base.getDate(), h || 0, m || 0));
    },
    [date, emitDateTime],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setViewMonth(date ?? new Date());
        if (!date) setTimeStr(currentDanishTime());
      }
    },
    [date],
  );

  const isDayDisabled = useCallback(
    (d: Date) => {
      if (minDate && d < minDate) return true;
      if (maxDate && d > maxDate) return true;
      return false;
    },
    [minDate, maxDate],
  );

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-left text-sm text-foreground ring-offset-background',
            'transition-colors placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !date && 'text-muted-foreground',
            !compact && 'min-w-[200px] sm:max-w-xs',
            className,
          )}
        >
          <span className="truncate">{displayText}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(
            'z-[250] w-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-md',
            compact ? 'p-2' : 'p-4',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
        >
          <DateTimePickerContent
            compact={compact}
            viewMonth={viewMonth}
            setViewMonth={setViewMonth}
            calendarDays={calendarDays}
            date={date}
            timeStr={timeStr}
            isDayDisabled={isDayDisabled}
            onDaySelect={handleDaySelect}
            onTimeChange={handleTimeChange}
            minTime={minTimeForInput}
            timezoneLabel={minDateToday ? 'Danish time' : undefined}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default DateTimePicker;
